import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import {
  createWalletClient,
  custom,
  getAddress,
  type Address,
  type EIP1193Provider,
  type WalletClient,
} from "viem";
import { baseSepolia } from "viem/chains";

type WalletValue = {
  address: Address | null;
  walletClient: WalletClient | null;
  connecting: boolean;
  error: string | null;
  connect: () => Promise<void>;
  disconnect: () => void;
  signVaultMessage: (message: string) => Promise<string>;
};

const WalletContext = createContext<WalletValue | null>(null);

type EventProvider = EIP1193Provider & {
  on?: (event: string, listener: (value: unknown) => void) => void;
  removeListener?: (event: string, listener: (value: unknown) => void) => void;
};

export function WalletProvider({ children }: { children: ReactNode }) {
  const [address, setAddress] = useState<Address | null>(null);
  const [walletClient, setWalletClient] = useState<WalletClient | null>(null);
  const [connecting, setConnecting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const attach = useCallback((rawAddress: string) => {
    if (!window.ethereum) return;
    const checksummed = getAddress(rawAddress);
    setAddress(checksummed);
    setWalletClient(
      createWalletClient({ account: checksummed, chain: baseSepolia, transport: custom(window.ethereum) }),
    );
  }, []);

  const connect = useCallback(async () => {
    if (!window.ethereum) {
      setError("Install an EVM wallet such as Coinbase Wallet or MetaMask.");
      return;
    }
    setConnecting(true);
    setError(null);
    try {
      const accounts = (await window.ethereum.request({ method: "eth_requestAccounts" })) as string[];
      if (!accounts[0]) throw new Error("The wallet returned no account.");
      try {
        await window.ethereum.request({
          method: "wallet_switchEthereumChain",
          params: [{ chainId: `0x${baseSepolia.id.toString(16)}` }],
        });
      } catch {
        throw new Error("Switch your wallet to Base Sepolia and try again.");
      }
      attach(accounts[0]);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Wallet connection failed.");
    } finally {
      setConnecting(false);
    }
  }, [attach]);

  const disconnect = useCallback(() => {
    setAddress(null);
    setWalletClient(null);
    setError(null);
  }, []);

  const signVaultMessage = useCallback(
    async (message: string) => {
      if (!walletClient || !address) throw new Error("Connect your wallet first.");
      return walletClient.signMessage({ account: address, message });
    },
    [walletClient, address],
  );

  useEffect(() => {
    if (!window.ethereum) return;
    window.ethereum.request({ method: "eth_accounts" }).then((accounts) => {
      const list = accounts as string[];
      if (list[0]) attach(list[0]);
    }).catch(() => undefined);

    const provider = window.ethereum as EventProvider;
    const onAccounts = (value: unknown) => {
      const accounts = value as string[];
      if (accounts[0]) attach(accounts[0]);
      else disconnect();
    };
    const onChain = () => window.location.reload();
    provider.on?.("accountsChanged", onAccounts);
    provider.on?.("chainChanged", onChain);
    return () => {
      provider.removeListener?.("accountsChanged", onAccounts);
      provider.removeListener?.("chainChanged", onChain);
    };
  }, [attach, disconnect]);

  const value = useMemo(
    () => ({ address, walletClient, connecting, error, connect, disconnect, signVaultMessage }),
    [address, walletClient, connecting, error, connect, disconnect, signVaultMessage],
  );
  return <WalletContext.Provider value={value}>{children}</WalletContext.Provider>;
}

export function useWallet() {
  const value = useContext(WalletContext);
  if (!value) throw new Error("useWallet must be used inside WalletProvider.");
  return value;
}
